import {FC, useEffect, useReducer, useRef, useState} from 'react'
import Logo from 'assets/images/DS-logo-white@2x.png'
import {useQueryClient} from '@tanstack/react-query'
import {useSelector} from 'react-redux'
import {Link} from 'react-router-dom'
import styled from 'styled-components'
import classNames from 'classnames'
import axios from 'axios'

import {dispatch} from 'app/store'
import {QuestionIcon, RenderIf, BellIcon} from 'components'
import {
  useNativeInfo,
  usePolicy,
  useSettingsDefaultPage,
  useUsersImported,
} from 'hooks'
import {NavLink} from './TopNavLink'
import {useMobileViewport} from 'hooks/useMobileViewport'
import {MobilePrimaryNavigation} from 'components/Mobile/MobilePrimaryNavigation'
import {selectUser, userActions} from 'features/User'
import {selectedEndpointDebugMode} from 'features/Endpoints/endpointsSelectors'
import {
  EndpointRoutesEnum,
  SSLType,
  PolicyRoutesEnum,
  CASBRoutesEnum,
  AccountList,
  InstallerType,
  SettingsRoutesEnum,
} from 'interfaces'
import {ArrowIcon, DownloadIcon} from 'components/Basic'
import {ToastCloseIcon} from 'components/Basic/SvgIcon/Native'
import {
  KEY_CODES,
  basePolicyRoutes,
  num0,
  secs10,
  settingsUsersRoute,
} from 'definitions/constant'
import HelpSection from './components/HelpSection'
import Toast from 'components/Basic/Toast'
import {history, isJWTTokenExpired, prefetchTranslations} from 'utils'
import {requestHandler} from 'services/api'
import Logger from 'services/Logger'
import NotificationSection from './components/NotificationSection'
import {LogoutContainer} from './components/LogoutContainer'
import {InstallerDownloadMenu} from './components/InstallerDownloadMenu'
import {subscribeToOpenInstallerDownload} from './installerDownloadEvents'

import {fetchNotifications} from './components/NotificationSection/service'
import {
  sslErrorActions,
  sslErrorNotificationInitState,
  sslErrorNotificationReducer,
} from './components/NotificationSection/reducer'
import {SSLErrorNotificationContext} from './components/NotificationSection/context'
import {prewarmEndpoints, useContextualPrefetch} from 'prefetch'
import {prewarmPolicies} from 'prefetch/prewarmPolicies'
import {CanIAccess, PERMISSIONS, withPermission} from 'rbac'
import {EndPoints, HttpMethods} from 'services/api/HTTP/HTTP.types'
import {useTranslation} from 'react-i18next'

enum TopNavigationTab {
  none,
  nameTab,
  download,
  help,
  notification,
}

const NavContainer = styled.div`
  width: 100%;
  height: 3.5rem;
`

let notificationInterval: ReturnType<typeof setInterval>

interface TopNavigationProps {
  isForFallback?: boolean
}

const downloadInstallerData = [
  {
    labelKey: 'common:WINDOWS_DOWNLOAD',
    key: InstallerType.Windows,
  },
  {
    labelKey: 'common:WINDOWS_ARM_DOWNLOAD',
    key: InstallerType.WindowsArm,
  },
  {
    labelKey: 'common:APPLE_DOWNLOAD',
    key: InstallerType.Mac,
  },
]

const avoidRoutes = [
  '/preview',
  '/settings/grant-access',
  CASBRoutesEnum.GRANT_ACCESS,
  CASBRoutesEnum.ACCESS_GRANTED,
  CASBRoutesEnum.ACCESS_DENIED,
  CASBRoutesEnum.SYNC_FAILURE,
  '/settings',
  '/tenants',
  '/callback',
]

export const TopNavigation: FC<TopNavigationProps> = ({
  isForFallback = false,
}) => {
  const isMobileViewport = useMobileViewport()
  // Rendered outside a Suspense boundary: fall back to English until loaded.
  const {t} = useTranslation('common', {useSuspense: false})
  const ProfileControl = isMobileViewport ? 'button' : 'span'
  const {pathname: currentRoute} = useNativeInfo()
  const {
    onboardingCompleted,
    name,
    emailId,
    downloadInstallerStatus,
    multiAccount,
  } = useSelector(selectUser)
  const {reauthRequired} = useUsersImported({
    pollWhileIncomplete: true,
  })

  const settingsDefaultPage = useSettingsDefaultPage()

  const queryClient = useQueryClient()

  const canReadNotifications = withPermission([PERMISSIONS.NOTIFICATIONS.READ])

  const {prefetch} = useContextualPrefetch()

  const [currentRightTab, setCurrentRightTab] = useState<TopNavigationTab>(
    TopNavigationTab.none,
  )
  const installerOpener = useRef<HTMLElement | null>(null)
  const [selectedBundle, setSelectedBundle] = useState<InstallerType | ''>('')
  const [selectedTenant, setSelectedTenant] = useState('')
  const [sslErrorNotificationState, sslErrorActionDispatcher] = useReducer(
    sslErrorNotificationReducer,
    sslErrorNotificationInitState,
  )
  const hasPendingNotification =
    sslErrorNotificationState?.totalAppCount > 0 ||
    sslErrorNotificationState?.totalUrlCount > 0

  const {isBasePolicyToBeColored} = usePolicy()
  const pathname = history.location.pathname

  const isDebugMode = useSelector(selectedEndpointDebugMode)

  const onLogout = async (e: React.MouseEvent<HTMLElement>): Promise<void> => {
    e.stopPropagation()
    notificationInterval && clearInterval(notificationInterval)
    setCurrentRightTab(TopNavigationTab.none)
    dispatch(userActions.logout())
  }

  useEffect(() => {
    const controller = new AbortController()
    const {signal: globalClickHandlerSignal} = controller

    const handleClick = (e: MouseEvent) => {
      const ids = ['logout', 'name', 'email', 'name icon', 'download-icon']
      const notificationEl = document.getElementById('notification-section')

      if (!ids.includes((e.target as HTMLElement)?.id ?? '')) {
        // safe way to close notification container without any regression
        if (notificationEl?.contains(e.target as Node)) {
          return
        }
        setCurrentRightTab(TopNavigationTab.none)
      }
    }

    window.addEventListener('click', handleClick, {
      signal: globalClickHandlerSignal,
    })
    return () => {
      controller.abort()
    }
  }, [])

  const onFileDownload = (bundle: InstallerType): void => {
    setSelectedBundle(bundle)
    dispatch(userActions.downloadInstaller({bundle}))
  }

  const onProfileClick = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation()
    setCurrentRightTab(TopNavigationTab.nameTab)
  }

  const onAccountSwitch = async (
    e: React.MouseEvent<HTMLElement>,
    value: string,
  ): Promise<void> => {
    let token = multiAccount?.details?.token
    setSelectedTenant(value)
    e.stopPropagation()
    // Check if switch account token is expired or not
    if (isJWTTokenExpired(token)) {
      try {
        // If token is expired, refresh the token
        const {data} = await requestHandler<AccountList>({
          method: HttpMethods.GET,
          url: EndPoints.SwitchAccount,
        })
        token = data.token
      } catch (error) {
        // Logout user if refresh switch account token fails. No-status
        // ERR_NETWORK / ECONNABORTED are allowlisted for the nav-mount
        // fetch, so report them here so a logout-causing switch is still
        // visible. Skip cancels and other no-status codes (interceptor
        // already handles status-bearing failures).
        if (
          axios.isAxiosError(error) &&
          !error.response &&
          (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')
        ) {
          Logger.LogError(
            new Error(
              'Account switch token refresh failed with a network error',
            ),
          )
        }
        dispatch(userActions.setTokensFailure())
      }
    }
    dispatch(
      userActions.setTokens({
        token,
        tenants: btoa(value),
        pageRefresh: true,
      }),
    )
  }

  const getSSLErrorDetails = async () => {
    sslErrorActionDispatcher(sslErrorActions.getSSLErrorNotifications())
    const result = await fetchNotifications({
      offset: num0,
      type: SSLType.app,
    })
    sslErrorActionDispatcher(
      sslErrorActions.getSSLErrorNotificationsSuccess(result),
    )
  }

  const startNotificationPolling = () => {
    if (!notificationInterval) {
      notificationInterval = setInterval(() => {
        getSSLErrorDetails()
      }, secs10)
    }
  }

  useEffect(() => {
    // prefetch common translation file on mount.
    // this change avoids page blink issue on SSL notification panel.
    prefetchTranslations(['common'])

    if (!isForFallback) {
      dispatch(userActions.getAccountsList())
    }
    return () => {
      setCurrentRightTab(TopNavigationTab.none)
      notificationInterval && clearInterval(notificationInterval)
    }
  }, [isForFallback])

  // this logic is stop polling when notification panel is visible
  useEffect(() => {
    if (!isForFallback) {
      // selected tab is not notification then start notification polling
      // else stop polling and do normal network call
      if (
        !notificationInterval &&
        currentRightTab !== TopNavigationTab.notification &&
        canReadNotifications
      ) {
        getSSLErrorDetails()
        startNotificationPolling()
      } else {
        notificationInterval && clearInterval(notificationInterval)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        notificationInterval = 0 as any
      }
    }
  }, [currentRightTab, canReadNotifications])

  // on policy listing screen selected policy is set to base policy
  // to avoid any sort of regression policy listing screen this local var is useful.
  const isTopBarToBeBlueColor =
    (isBasePolicyToBeColored && basePolicyRoutes.includes(currentRoute)) ||
    isDebugMode

  useEffect(
    () =>
      subscribeToOpenInstallerDownload(() => {
        installerOpener.current =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null
        setCurrentRightTab(TopNavigationTab.download)
        // Opened from a page button below the menu, so move keyboard focus
        // into it once it has rendered.
        window.setTimeout(() => {
          document
            .querySelector<HTMLElement>(
              '[data-cy="top-navigation-dropdown"] [role="button"][tabindex="0"]',
            )
            ?.focus()
        }, 0)
      }),
    [],
  )

  useEffect(() => {
    if (currentRightTab !== TopNavigationTab.download) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.key !== KEY_CODES.ESCAPE) return
      event.preventDefault()
      setCurrentRightTab(TopNavigationTab.none)
      if (installerOpener.current?.isConnected) installerOpener.current.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [currentRightTab])

  const iconClick = (selectedTab: TopNavigationTab) => {
    if (
      selectedTab === TopNavigationTab.download &&
      currentRightTab !== selectedTab
    ) {
      installerOpener.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
    }
    currentRightTab === selectedTab
      ? setCurrentRightTab(TopNavigationTab.none)
      : setCurrentRightTab(selectedTab)
  }

  const onNavClick = (path: string): void => {
    switch (path) {
      case EndpointRoutesEnum.BASE:
        prewarmEndpoints(queryClient, prefetch)
        break
      case PolicyRoutesEnum.BASE:
        prewarmPolicies(prefetch)
        break
      default:
        break
    }
  }

  return (
    <>
      <NavContainer
        className={classNames(
          'w-screen  flex justify-between items-center border-b-0.5 border-gray-400 relative px-6',
          {
            hidden: !onboardingCompleted || avoidRoutes.includes(currentRoute),
            'bg-dope-teal-100': isTopBarToBeBlueColor,
            'bg-dope-black': !isTopBarToBeBlueColor,
          },
        )}
        id="top-navigation"
        data-cy="top-navigation-bar"
      >
        {/* Check reauth sync status and show reauth toast */}
        <RenderIf
          isTrue={!currentRoute?.includes(SettingsRoutesEnum.GRANT_ACCESS)}
        >
          <CheckUserImportStatus
            currentRoute={currentRoute}
            reAuthRequired={reauthRequired}
          />
        </RenderIf>
        <div className="mobile-navigation-brand flex items-center">
          <Link to="/analytics/overview" className="mr-7">
            <img src={Logo} alt="logo" className="object-contain w-8 h-8" />
          </Link>
          <MobilePrimaryNavigation
            onOpen={() => setCurrentRightTab(TopNavigationTab.none)}
          >
            <NavLink
              pathname="/analytics/overview"
              current={pathname.includes('analytics')}
              linkName="Analytics"
            />
            <NavLink
              pathname="/policies"
              onClick={() => onNavClick(PolicyRoutesEnum.BASE)}
              current={pathname.includes('policies')}
              linkName="SWG"
            />
            <NavLink
              pathname={CASBRoutesEnum.BASE}
              current={pathname.includes('casb')}
              linkName="CASB"
            />
            <NavLink
              pathname="/endpoints"
              current={pathname === '/endpoints'}
              linkName="Endpoints"
              onClick={() => onNavClick(EndpointRoutesEnum.BASE)}
            />
            <NavLink
              pathname={settingsDefaultPage}
              current={pathname.includes('settings')}
              linkName="Settings"
            />
          </MobilePrimaryNavigation>
        </div>

        <ul className="flex items-center text-white abc-regular h-full">
          {/* The Dopamine launcher is portalled here, out of the Assistant
              root, so it gets the Assistant's colour tokens on phones. */}
          <li
            id="mobile-assistant-launcher"
            className="mobile-assistant-launcher mobile:[--dopamine-accent-bright:#dcccff] mobile:[--dopamine-accent-rgb:201_182_242] mobile:[--dopamine-accent-soft:#b79bff] mobile:[--dopamine-accent:theme(colors.dopamine.accent)] mobile:[--dopamine-muted:theme(colors.dopamine.muted)]"
          />
          <CanIAccess reqPermissions={[PERMISSIONS.NOTIFICATIONS.READ]}>
            <li
              className={classNames(
                'mr-4 h-full cursor-pointer mobile:hidden',
                {
                  'border-b-3 border-white':
                    currentRightTab === TopNavigationTab.notification,
                },
              )}
            >
              <button
                data-cy="notification-bell-icon"
                aria-label="Click Notification Bell Icon to view SSL Pinning Errors"
                className="h-14 relative"
                onClick={e => {
                  e.stopPropagation()
                  iconClick(TopNavigationTab.notification)
                }}
              >
                <BellIcon classes="hover:text-white" />
                <RenderIf
                  isTrue={
                    sslErrorNotificationState?.totalAppCount > 0 ||
                    sslErrorNotificationState?.totalUrlCount > 0
                  }
                >
                  <span
                    data-cy="pending-notification-indicator"
                    className={classNames(
                      'h-2.5 w-2.5 flex justify-center items-center absolute right-0.5 top-3.5',
                      {
                        'bg-dope-teal-100': isTopBarToBeBlueColor,
                        'bg-dope-black': !isTopBarToBeBlueColor,
                      },
                    )}
                  >
                    <span className="relative h-1.5 w-1.5 flex">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-dope-purple-50 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-dope-purple-50"></span>
                    </span>
                  </span>
                </RenderIf>
              </button>
            </li>
          </CanIAccess>
          <li
            className={classNames('mr-4 h-full cursor-pointer mobile:hidden', {
              'border-b-3 border-white':
                currentRightTab === TopNavigationTab.download,
            })}
          >
            <button
              id="download-icon"
              aria-label={
                isMobileViewport ? t('DOWNLOAD_ENDPOINT_INSTALLER') : undefined
              }
              className="h-14"
              onClick={() => iconClick(TopNavigationTab.download)}
            >
              <DownloadIcon
                strokeColor="text-gray-1000"
                strokeWidth={1}
                id="download-icon"
                width={24}
                height={24}
                classes={'hover:text-white'}
              />
            </button>
          </li>
          <li
            className={classNames('mr-4 h-full cursor-pointer mobile:hidden', {
              'border-b-3 border-white':
                currentRightTab === TopNavigationTab.help,
            })}
          >
            <button
              id="question-icon"
              aria-label={isMobileViewport ? t('HELP') : undefined}
              className="h-14"
              onClick={e => {
                e.stopPropagation()
                iconClick(TopNavigationTab.help)
              }}
            >
              <QuestionIcon
                strokeColor="text-gray-1000"
                width={24}
                height={24}
                classes={'hover:text-white'}
              />
            </button>
          </li>
          <li
            className={classNames('cursor-pointer h-14 pt-4', {
              'border-b-3 border-white':
                currentRightTab === TopNavigationTab.nameTab,
            })}
          >
            <ProfileControl
              {...(isMobileViewport
                ? {
                    type: 'button' as const,
                    'aria-label': t('ACCOUNT_MENU'),
                  }
                : {})}
              id="name icon"
              data-cy="profile-icon"
              onClick={(e: React.MouseEvent<HTMLElement>) =>
                currentRightTab === TopNavigationTab.nameTab
                  ? setCurrentRightTab(TopNavigationTab.none)
                  : onProfileClick(e)
              }
              className="rounded-full w-6 h-6 bg-dope-cyan-200 hover:bg-dope-cyan-100 flex justify-center items-center uppercase"
            >
              {name?.charAt(0) || '?'}
            </ProfileControl>
          </li>
        </ul>
      </NavContainer>
      {(currentRightTab === TopNavigationTab.nameTab ||
        currentRightTab === TopNavigationTab.download ||
        currentRightTab === TopNavigationTab.help ||
        currentRightTab === TopNavigationTab.notification) && (
        <div
          data-cy="top-navigation-dropdown"
          className={classNames(
            'h-full z-40 fixed w-full top-14 bg-dope-black bg-opacity-0',
            {
              hidden:
                !onboardingCompleted || avoidRoutes.includes(currentRoute),
            },
          )}
        >
          <div className="grid-cols-14">
            <div className="col-start-3 grid grid-cols-14 col-span-14">
              <RenderIf
                isTrue={currentRightTab === TopNavigationTab.notification}
              >
                <SSLErrorNotificationContext.Provider
                  value={[sslErrorNotificationState, sslErrorActionDispatcher]}
                >
                  <NotificationSection
                    isTopBarToBeBlueColor={isTopBarToBeBlueColor}
                  />
                </SSLErrorNotificationContext.Provider>
              </RenderIf>
              <RenderIf isTrue={currentRightTab === TopNavigationTab.help}>
                <HelpSection isTopBarToBeBlueColor={isTopBarToBeBlueColor} />
              </RenderIf>
              <RenderIf isTrue={currentRightTab === TopNavigationTab.nameTab}>
                <LogoutContainer
                  name={name}
                  pathname={pathname}
                  emailId={emailId}
                  selectedTenant={selectedTenant}
                  onLogout={onLogout}
                  onAccountSwitch={onAccountSwitch}
                  isTopBarToBeBlueColor={isTopBarToBeBlueColor}
                  mobileShortcuts={
                    isMobileViewport
                      ? {
                          canReadNotifications: canReadNotifications,
                          hasPendingNotification: hasPendingNotification,
                          onNotifications: () =>
                            setCurrentRightTab(TopNavigationTab.notification),
                          onDownload: () =>
                            setCurrentRightTab(TopNavigationTab.download),
                          onHelp: () =>
                            setCurrentRightTab(TopNavigationTab.help),
                        }
                      : undefined
                  }
                />
              </RenderIf>
              <RenderIf isTrue={currentRightTab === TopNavigationTab.download}>
                <InstallerDownloadMenu
                  options={downloadInstallerData}
                  isTopBarToBeBlueColor={isTopBarToBeBlueColor}
                  downloadInstallerStatus={downloadInstallerStatus}
                  selectedBundle={selectedBundle}
                  onDownload={onFileDownload}
                />
              </RenderIf>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

interface CheckUserImportStatusProps {
  reAuthRequired: boolean
  currentRoute: string
}
const CheckUserImportStatus: FC<CheckUserImportStatusProps> = ({
  reAuthRequired,
  currentRoute,
}) => {
  const [showToastOnce, setShowToastOnce] = useState<boolean>(true)

  const canIManageUsers = withPermission([PERMISSIONS.USER_MANAGEMENT.WRITE])

  useEffect(() => {
    if (reAuthRequired && showToastOnce && canIManageUsers) {
      const isSettingsUsersPage = currentRoute === settingsUsersRoute
      const reAuthMessage = () => (
        // Phones wrap the sentence; one line would be cut off at the edge.
        <div className="flex items-center gap-3 whitespace-nowrap mobile:items-start mobile:whitespace-normal">
          <ToastCloseIcon
            width={20}
            height={20}
            strokeColor="text-yellow-100"
          />
          <span>
            User import is no longer synced. Please reconnect to reinstate
            auto-sync of users.{' '}
            <RenderIf isTrue={!isSettingsUsersPage}>
              <span
                onClick={() => {
                  history.push(settingsUsersRoute)
                }}
                className="text-yellow-100 cursor-pointer hover:underline decoration-0 whitespace-nowrap"
              >
                Take me there{' '}
                <ArrowIcon
                  height={12}
                  width={24}
                  classes="text-yellow-100 inline-block"
                  strokeWidth={0.1}
                />
              </span>
            </RenderIf>
          </span>
        </div>
      )
      Toast({
        message: reAuthMessage,
        type: 'warning',
        style: {width: 'fit-content'},
        autoClose: false,
        closeButton: false,
        hideIcon: true,
      })
      setShowToastOnce(false)
    }
  }, [reAuthRequired, showToastOnce])

  return <></>
}
